export const COMMERCIAL_INVENTORY_BATCH_SIZE = 8;

const normalizeText = (value) => String(value ?? '').trim();
const normalizeIdentity = (value) => normalizeText(value).toLowerCase();
const INVENTORY_CANDIDATE_ZONE_TYPES = new Set([
  'commercial',
  'industrial',
  'agricultural',
]);
const INVENTORY_CASH_PROFILES = new Set([
  'Modest',
  'Standard',
  'Prosperous',
  'Elite',
]);

export const getInventoryDraftPlaces = (
  draftPlace,
  draftChildSuggestions = []
) => {
  const places = [
    draftPlace,
    ...(Array.isArray(draftChildSuggestions) ? draftChildSuggestions : []),
  ];
  const seenPlaceIds = new Set();

  return places
    .filter((place) =>
      INVENTORY_CANDIDATE_ZONE_TYPES.has(normalizeIdentity(place?.zoneType))
    )
    .map((place) => ({
      ...place,
      idEntity: normalizeText(
        place?.draftPlaceId ?? place?.idEntity ?? 'draft-place:root'
      ),
      name: normalizeText(
        place?.name ?? place?.title ?? place?.entityName ?? place?.entityTitle
      ),
    }))
    .filter((place) => {
      if (!place.idEntity || !place.name || seenPlaceIds.has(place.idEntity)) {
        return false;
      }
      seenPlaceIds.add(place.idEntity);
      return true;
    });
};

export const getCommercialDraftPlaces = getInventoryDraftPlaces;

export const createCommercialInventorySlotId = (
  commercialPlaceId,
  inventoryTypeKey,
  index
) => `${commercialPlaceId}::${inventoryTypeKey}::${index}`;

export const getCommercialInventorySlots = (manifest) =>
  (Array.isArray(manifest?.commercialPlaceInventories)
    ? manifest.commercialPlaceInventories
    : [])
    .flatMap((placeInventory) =>
      (Array.isArray(placeInventory?.inventoryTypes)
        ? placeInventory.inventoryTypes
        : [])
        .flatMap((inventoryType) => {
          const itemCount = Math.max(
            0,
            Math.floor(Number(inventoryType?.itemCount) || 0)
          );
          return Array.from({ length: itemCount }, (_, index) => ({
            inventorySlotId: createCommercialInventorySlotId(
              normalizeText(placeInventory?.commercialPlaceId),
              normalizeText(inventoryType?.inventoryTypeKey),
              index + 1
            ),
            commercialPlaceId: normalizeText(
              placeInventory?.commercialPlaceId
            ),
            commercialPlaceName: normalizeText(
              placeInventory?.commercialPlaceName
            ),
            inventoryTypeKey: normalizeText(
              inventoryType?.inventoryTypeKey
            ),
            inventoryType: normalizeText(inventoryType?.inventoryType),
          }));
        })
    );

export const validateCommercialInventoryManifest = (
  manifest,
  commercialPlaces
) => {
  if (!manifest || typeof manifest !== 'object') {
    throw new Error('The inventory planner returned no manifest.');
  }

  const expectedPlaces = Array.isArray(commercialPlaces)
    ? commercialPlaces
    : [];
  const expectedPlacesById = new Map(
    expectedPlaces.map((place) => [
      normalizeText(place?.idEntity),
      {
        name: normalizeText(place?.name),
        zoneType: normalizeIdentity(place?.zoneType),
      },
    ])
  );
  const placeInventories = Array.isArray(
    manifest?.commercialPlaceInventories
  )
    ? manifest.commercialPlaceInventories
    : null;
  if (!placeInventories) {
    throw new Error('The inventory planner returned no commercial places.');
  }

  const seenPlaceIds = new Set();
  placeInventories.forEach((placeInventory) => {
    const placeId = normalizeText(placeInventory?.commercialPlaceId);
    const expectedPlace = expectedPlacesById.get(placeId);
    if (!expectedPlace) {
      throw new Error(`The inventory plan contains unknown place ${placeId}.`);
    }
    const expectedName = expectedPlace.name;
    if (normalizeText(placeInventory?.commercialPlaceName) !== expectedName) {
      throw new Error(
        `The inventory plan must preserve the exact name ${expectedName}.`
      );
    }
    if (seenPlaceIds.has(placeId)) {
      throw new Error(`The inventory plan repeats ${expectedName}.`);
    }
    seenPlaceIds.add(placeId);

    if (typeof placeInventory?.stocksTradeableItems !== 'boolean') {
      throw new Error(
        `${expectedName} has no valid trade inventory decision.`
      );
    }
    if (!normalizeText(placeInventory?.inventoryDecision)) {
      throw new Error(`${expectedName} has no inventory decision rationale.`);
    }
    if (
      expectedPlace.zoneType === 'commercial' &&
      !placeInventory.stocksTradeableItems
    ) {
      throw new Error(
        `Commercial place ${expectedName} must stock tradeable items.`
      );
    }
    const cashReserveRatio = Number(placeInventory?.cashReserveRatio);
    const cashReserveProfile = normalizeText(
      placeInventory?.cashReserveProfile
    );
    if (placeInventory.stocksTradeableItems) {
      if (
        !Number.isFinite(cashReserveRatio) ||
        cashReserveRatio < 0.2 ||
        cashReserveRatio > 0.45
      ) {
        throw new Error(
          `${expectedName} cashReserveRatio must be between 0.20 and 0.45.`
        );
      }
      if (!INVENTORY_CASH_PROFILES.has(cashReserveProfile)) {
        throw new Error(`${expectedName} has an invalid cash reserve profile.`);
      }
    } else if (cashReserveRatio !== 0 || cashReserveProfile !== 'None') {
      throw new Error(
        `${expectedName} is not stocked and must use cashReserveRatio 0 and cashReserveProfile None.`
      );
    }

    const inventoryTypes = Array.isArray(placeInventory?.inventoryTypes)
      ? placeInventory.inventoryTypes
      : null;
    if (!inventoryTypes) {
      throw new Error(`${expectedName} has no inventoryTypes array.`);
    }
    if (
      placeInventory.stocksTradeableItems &&
      inventoryTypes.length === 0
    ) {
      throw new Error(
        `${expectedName} stocks tradeable items but has no planned inventory types.`
      );
    }
    if (
      !placeInventory.stocksTradeableItems &&
      inventoryTypes.length > 0
    ) {
      throw new Error(
        `${expectedName} is not stocked but contains planned inventory types.`
      );
    }
    inventoryTypes.forEach((inventoryType) => {
      const itemCount = Number(inventoryType?.itemCount);
      if (!Number.isInteger(itemCount) || itemCount < 1) {
        throw new Error(
          `${expectedName} has an invalid inventory item count.`
        );
      }
    });
  });

  if (
    seenPlaceIds.size !== expectedPlacesById.size ||
    [...expectedPlacesById.keys()].some((placeId) => !seenPlaceIds.has(placeId))
  ) {
    throw new Error(
      'The inventory plan must include every Commercial, Industrial, and ' +
        'Agricultural candidate exactly once.'
    );
  }

  return manifest;
};

export const getCommercialInventoryProgress = (
  manifest,
  itemDrafts = []
) => {
  const completedSlotIds = new Set(
    (Array.isArray(itemDrafts) ? itemDrafts : [])
      .map((item) => normalizeText(item?.inventorySlotId))
      .filter(Boolean)
  );
  const placeInventories = (
    Array.isArray(manifest?.commercialPlaceInventories)
      ? manifest.commercialPlaceInventories
      : []
  ).map((placeInventory) => {
    const slots = getCommercialInventorySlots({
      commercialPlaceInventories: [placeInventory],
    });
    const generatedCount = slots.filter((slot) =>
      completedSlotIds.has(slot.inventorySlotId)
    ).length;

    return {
      ...placeInventory,
      targetCount: slots.length,
      generatedCount,
      complete:
        !placeInventory?.stocksTradeableItems ||
        (slots.length > 0 && generatedCount === slots.length),
    };
  });
  const targetCount = placeInventories.reduce(
    (total, placeInventory) => total + placeInventory.targetCount,
    0
  );
  const generatedCount = placeInventories.reduce(
    (total, placeInventory) => total + placeInventory.generatedCount,
    0
  );

  return {
    targetCount,
    generatedCount,
    complete:
      placeInventories.length > 0 && generatedCount === targetCount,
    stockedPlaceCount: placeInventories.filter(
      (placeInventory) => placeInventory.stocksTradeableItems
    ).length,
    placeInventories,
  };
};

export const getNextCommercialInventoryBatch = (
  manifest,
  itemDrafts = [],
  batchSize = COMMERCIAL_INVENTORY_BATCH_SIZE
) => {
  const normalizedBatchSize = Math.max(
    1,
    Math.floor(Number(batchSize) || COMMERCIAL_INVENTORY_BATCH_SIZE)
  );
  const completedSlotIds = new Set(
    (Array.isArray(itemDrafts) ? itemDrafts : [])
      .map((item) => normalizeText(item?.inventorySlotId))
      .filter(Boolean)
  );
  const requestedSlots = getCommercialInventorySlots(manifest)
    .filter((slot) => !completedSlotIds.has(slot.inventorySlotId))
    .slice(0, normalizedBatchSize);

  return requestedSlots.length > 0 ? requestedSlots : null;
};

export const mergeCommercialInventoryItemBatches = (
  existingItemDrafts,
  returnedItemDrafts,
  requestedSlots
) => {
  const currentItems = Array.isArray(existingItemDrafts)
    ? existingItemDrafts
    : [];
  const returnedItems = Array.isArray(returnedItemDrafts)
    ? returnedItemDrafts
    : [];
  const slots = Array.isArray(requestedSlots) ? requestedSlots : [];
  const slotsById = new Map(
    slots.map((slot) => [normalizeText(slot?.inventorySlotId), slot])
  );

  if (
    returnedItems.length !== slots.length ||
    slotsById.size !== slots.length
  ) {
    throw new Error(
      'Inventory generation did not return every assigned slot.'
    );
  }

  const completedSlotIds = new Set(
    currentItems
      .map((item) => normalizeText(item?.inventorySlotId))
      .filter(Boolean)
  );
  const itemNames = new Set(
    currentItems
      .map((item) => normalizeIdentity(item?.name))
      .filter(Boolean)
  );
  const returnedSlotIds = new Set();

  returnedItems.forEach((item) => {
    const slotId = normalizeText(item?.inventorySlotId);
    const slot = slotsById.get(slotId);
    if (
      !slot ||
      completedSlotIds.has(slotId) ||
      returnedSlotIds.has(slotId)
    ) {
      throw new Error(
        `Inventory generation returned invalid slot ${slotId || '(missing)'}.`
      );
    }
    if (
      normalizeText(item?.commercialPlaceId) !== slot.commercialPlaceId ||
      normalizeText(item?.inventoryTypeKey) !== slot.inventoryTypeKey
    ) {
      throw new Error(
        `Inventory slot ${slotId} was assigned to the wrong shop or stock type.`
      );
    }

    const itemName = normalizeIdentity(item?.name);
    if (!itemName || itemNames.has(itemName)) {
      throw new Error(
        `Inventory generation returned a missing or duplicate item name.`
      );
    }

    returnedSlotIds.add(slotId);
    itemNames.add(itemName);
  });

  if (
    returnedSlotIds.size !== slotsById.size ||
    [...slotsById.keys()].some((slotId) => !returnedSlotIds.has(slotId))
  ) {
    throw new Error(
      'Inventory generation did not return every assigned slot.'
    );
  }

  return [...currentItems, ...returnedItems];
};

export const generateCommercialInventoryBatchesSerially = async ({
  manifest,
  itemDrafts = [],
  batchSize = COMMERCIAL_INVENTORY_BATCH_SIZE,
  requestBatch,
  onBatch,
  shouldContinue,
} = {}) => {
  if (typeof requestBatch !== 'function') {
    throw new Error('An inventory batch request function is required.');
  }

  let currentItems = Array.isArray(itemDrafts) ? itemDrafts : [];
  let batchCount = 0;
  let stopped = false;

  while (true) {
    if (typeof shouldContinue === 'function' && !shouldContinue()) {
      stopped = true;
      break;
    }

    const requestedSlots = getNextCommercialInventoryBatch(
      manifest,
      currentItems,
      batchSize
    );
    if (!requestedSlots) {
      break;
    }

    const returnedItems = await requestBatch({
      requestedSlots,
      currentItemDrafts: currentItems,
      batchNumber: batchCount + 1,
    });
    const nextItems = mergeCommercialInventoryItemBatches(
      currentItems,
      returnedItems,
      requestedSlots
    );
    currentItems = nextItems;
    batchCount += 1;

    if (typeof onBatch === 'function') {
      await onBatch({
        requestedSlots,
        returnedItemDrafts: returnedItems,
        itemDrafts: currentItems,
        batchNumber: batchCount,
      });
    }
  }

  return {
    itemDrafts: currentItems,
    batchCount,
    stopped,
  };
};
